import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, Send, ImagePlus, Loader2 } from "lucide-react";
import { api, type ChatMessage } from "@/lib/api";
import { supabase } from "@/lib/supabase";

interface Props {
  caseId: string;
  /** 自分の立場。業者アプリでは "vendor"。吹き出しの左右判定に使う。 */
  myRole: "customer" | "vendor";
  /** チャット見出し（相手の名前）。未指定なら「お客様とのチャット」。 */
  title?: string;
  /** 入力欄の下に出す常設アクション（日程調整・完了報告・入金など）。LINE風に完結させるため。 */
  footer?: ReactNode;
}

/**
 * 直チャット（業者⇔顧客）。受注後の案件で日程調整・補足確認に使う。
 * messages テーブル(sql/12)をポーリング。当事者のみ閲覧/投稿可（RLS）。
 */
export function ChatPanel({ caseId, myRole, title, footer }: Props) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const imgInputRef = useRef<HTMLInputElement>(null);
  const sendImg = useMutation({
    mutationFn: (file: File) => api.sendMessageImage(caseId, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["messages", caseId] }),
  });

  const { data: msgs } = useQuery({
    queryKey: ["messages", caseId],
    queryFn: () => api.listMessages(caseId),
    refetchInterval: 15_000, // Realtime主・ポーリングは保険
  });

  // Realtime：相手のメッセージが届いたら即時反映（LINEのように待たずに表示）
  useEffect(() => {
    const ch = supabase
      .channel(`messages:${caseId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages", filter: `case_id=eq.${caseId}` },
        () => { qc.invalidateQueries({ queryKey: ["messages", caseId] }); },
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [caseId, qc]);

  const sendMut = useMutation({
    mutationFn: (body: string) => api.sendMessage(caseId, body),
    onSuccess: () => {
      setText("");
      qc.invalidateQueries({ queryKey: ["messages", caseId] });
    },
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs?.length]);

  const otherLabel = title ?? (myRole === "vendor" ? "お客様" : "業者");
  const submit = () => {
    const b = text.trim();
    if (b) sendMut.mutate(b);
  };

  return (
    <div className="rounded-2xl border border-[var(--color-border)] p-4 space-y-3">
      <p className="text-sm font-semibold flex items-center gap-1.5">
        <MessageCircle size={16} className="text-brand-600 dark:text-brand-300" />
        {title ? title : `${otherLabel}とのチャット`}
      </p>
      <p className="text-[11px] text-ink-soft dark:text-white/50">
        工事日の調整や補足の確認にご利用ください。
      </p>

      <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
        {(!msgs || msgs.length === 0) && (
          <p className="text-xs text-ink-soft dark:text-white/60 py-4 text-center">
            メッセージはまだありません。最初の一言を送ってみましょう。
          </p>
        )}
        {msgs?.map((m) => (
          <Bubble key={m.id} m={m} mine={m.sender_role === myRole} imgUrl={m.image_url ?? undefined} />
        ))}
        <div ref={endRef} />
      </div>

      <div className="flex items-end gap-2">
        <input
          ref={imgInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) sendImg.mutate(f); e.target.value = ""; }}
        />
        <button
          type="button"
          onClick={() => imgInputRef.current?.click()}
          disabled={sendImg.isPending}
          className="shrink-0 border border-[var(--color-border)] rounded-xl p-2.5 text-brand-600 dark:text-brand-300 disabled:opacity-50 active:scale-95 transition"
          aria-label="画像を送る"
        >
          {sendImg.isPending ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
        </button>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
          }}
          rows={1}
          placeholder="メッセージを入力（⌘/Ctrl+Enterで送信）"
          className="flex-1 resize-none border border-[var(--color-border)] rounded-xl px-3 py-2 bg-transparent text-sm"
        />
        <button
          onClick={submit}
          disabled={!text.trim() || sendMut.isPending}
          className="shrink-0 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white rounded-xl p-2.5 active:scale-95 transition"
          aria-label="送信"
        >
          <Send size={16} />
        </button>
      </div>
      {(sendMut.error || sendImg.error) && (
        <p className="text-xs text-red-600">{((sendMut.error || sendImg.error) as Error).message}</p>
      )}
      {footer && (
        <div className="pt-2 border-t border-[var(--color-border)] space-y-2">{footer}</div>
      )}
    </div>
  );
}

function Bubble({ m, mine, imgUrl }: { m: ChatMessage; mine: boolean; imgUrl?: string }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
          mine
            ? "bg-brand-500 text-white rounded-br-sm"
            : "bg-gray-100 dark:bg-white/10 rounded-bl-sm"
        }`}
      >
        {imgUrl && (
          <a href={imgUrl} target="_blank" rel="noopener noreferrer"><img src={imgUrl} alt="送信画像" className="rounded-lg max-w-[200px] max-h-[240px] object-cover mb-1" /></a>
        )}
        {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
        <p
          className={`text-[10px] mt-1 ${
            mine ? "text-white/70" : "text-ink-soft dark:text-white/50"
          }`}
        >
          {new Date(m.created_at).toLocaleString("ja-JP", {
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </div>
    </div>
  );
}
